import { CARDS, CATEGORIES, LEVELS, getLevel, type Card, type Category, type Level } from "./deck";
import { CardState, freshState, isDue, masteryLevel } from "./scheduler";

export type Mode = "mc" | "conj" | "sentence";
export type Direction = "hr-de" | "de-hr" | "mix";

export interface Settings {
  dir: Direction;
  audio: boolean;
  newPerDay: number;
  disabledCats: Category[];
  allowedLevels: Level[];
  mode: Mode;
  autoAdvanceMs: number;
}

export interface Session {
  date: string;
  done: number;
  correct: number;
  wrong: number;
  streak: number;
  best: number;
  introducedByMode: Record<Mode, number>;
}

export interface AppState {
  cards: Record<string, CardState>;
  settings: Settings;
  session: Session;
}

const STORAGE_KEY = "hr_a1_quiz_v1";

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function defaultSettings(): Settings {
  return {
    dir: "mix",
    audio: true,
    newPerDay: 20,
    disabledCats: [],
    allowedLevels: ["A1"],
    mode: "mc",
    autoAdvanceMs: 3000,
  };
}

function defaultSession(): Session {
  return {
    date: todayKey(),
    done: 0,
    correct: 0,
    wrong: 0,
    streak: 0,
    best: 0,
    introducedByMode: { mc: 0, conj: 0, sentence: 0 },
  };
}

function defaultState(): AppState {
  const cards: Record<string, CardState> = {};
  for (const c of CARDS) cards[c.id] = freshState();
  return { cards, settings: defaultSettings(), session: defaultSession() };
}

function migrate(state: AppState): AppState {
  for (const c of CARDS) {
    if (!state.cards[c.id]) state.cards[c.id] = freshState();
  }
  if (!state.settings) state.settings = defaultSettings();
  if (state.settings.mode === undefined) state.settings.mode = "mc";
  if (!Array.isArray(state.settings.disabledCats)) state.settings.disabledCats = [];
  if (typeof state.settings.newPerDay !== "number") state.settings.newPerDay = 20;
  if (typeof state.settings.audio !== "boolean") state.settings.audio = true;
  if (typeof state.settings.autoAdvanceMs !== "number") state.settings.autoAdvanceMs = 3000;
  if (!state.settings.dir) state.settings.dir = "mix";
  if (!Array.isArray(state.settings.allowedLevels) || state.settings.allowedLevels.length === 0) {
    state.settings.allowedLevels = ["A1"];
  }
  const validLevels = new Set<Level>(LEVELS);
  state.settings.allowedLevels = state.settings.allowedLevels.filter(l => validLevels.has(l));
  if (state.settings.allowedLevels.length === 0) state.settings.allowedLevels = ["A1"];

  if (!state.session || state.session.date !== todayKey()) {
    state.session = defaultSession();
  }
  const anyLegacy = state.session as Session & { introduced?: number };
  if (!state.session.introducedByMode || typeof state.session.introducedByMode !== "object") {
    state.session.introducedByMode = { mc: 0, conj: 0, sentence: 0 };
  }
  if (typeof state.session.introducedByMode.mc !== "number") state.session.introducedByMode.mc = 0;
  if (typeof state.session.introducedByMode.conj !== "number") state.session.introducedByMode.conj = 0;
  if (typeof state.session.introducedByMode.sentence !== "number") state.session.introducedByMode.sentence = 0;
  if (typeof anyLegacy.introduced === "number") {
    state.session.introducedByMode.mc += anyLegacy.introduced;
    delete anyLegacy.introduced;
  }
  return state;
}

export class Store {
  state: AppState;
  private listeners: Array<() => void> = [];

  constructor() {
    this.state = this.load();
  }

  private load(): AppState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      const parsed = JSON.parse(raw) as AppState;
      return migrate(parsed);
    } catch {
      return defaultState();
    }
  }

  save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // ignore quota/security errors
    }
    for (const fn of this.listeners) fn();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }

  reset(): void {
    this.state = defaultState();
    this.save();
  }

  export(): string {
    return JSON.stringify(this.state, null, 2);
  }

  import(json: string): boolean {
    try {
      const parsed = JSON.parse(json) as AppState;
      if (!parsed || typeof parsed !== "object") return false;
      if (!parsed.cards || !parsed.settings || !parsed.session) return false;
      this.state = migrate(parsed);
      this.save();
      return true;
    } catch {
      return false;
    }
  }

  activeCards(): Card[] {
    const disabled = new Set(this.state.settings.disabledCats);
    const allowed = new Set<Level>(this.state.settings.allowedLevels);
    const mode = this.state.settings.mode;
    return CARDS.filter(c => {
      if (disabled.has(c.cat)) return false;
      if (!allowed.has(getLevel(c))) return false;
      if (mode === "conj") return c.cat === "Konjugacija";
      if (mode === "sentence") return c.cat === "Rečenice";
      return c.cat !== "Konjugacija" && c.cat !== "Rečenice";
    });
  }

  getDueCount(): number {
    const now = Date.now();
    return this.activeCards().filter(c => isDue(this.state.cards[c.id], now)).length;
  }

  getNewCount(): number {
    return this.activeCards().filter(c => !this.state.cards[c.id].seen).length;
  }

  pickNext(): Card | null {
    const active = this.activeCards();
    const mode = this.state.settings.mode;
    const now = Date.now();
    const due = active
      .filter(c => isDue(this.state.cards[c.id], now))
      .sort((a, b) => this.state.cards[a.id].due - this.state.cards[b.id].due);
    if (due.length > 0) {
      const head = due.slice(0, 5);
      return head[Math.floor(Math.random() * head.length)];
    }
    if (this.state.session.introducedByMode[mode] < this.state.settings.newPerDay) {
      const news = active.filter(c => !this.state.cards[c.id].seen);
      if (news.length > 0) {
        const c = news[Math.floor(Math.random() * news.length)];
        this.state.session.introducedByMode[mode]++;
        return c;
      }
    }
    const seen = active
      .filter(c => this.state.cards[c.id].seen)
      .sort((a, b) => this.state.cards[a.id].due - this.state.cards[b.id].due);
    if (seen.length > 0) return seen[0];
    return null;
  }

  bumpNewQuota(extra: number = 10): void {
    this.state.settings.newPerDay += extra;
  }

  recordResult(cardId: string, correct: boolean): void {
    this.state.session.done++;
    if (correct) {
      this.state.session.correct++;
      this.state.session.streak++;
      if (this.state.session.streak > this.state.session.best) {
        this.state.session.best = this.state.session.streak;
      }
    } else {
      this.state.session.wrong++;
      this.state.session.streak = 0;
    }
    void cardId;
  }

  masteryByCategory(): Array<{ cat: Category; total: number; mastered: number }> {
    const allowed = new Set<Level>(this.state.settings.allowedLevels);
    const out: Array<{ cat: Category; total: number; mastered: number }> = [];
    for (const cat of CATEGORIES) {
      const cards = CARDS.filter(c => c.cat === cat && allowed.has(getLevel(c)));
      const mastered = cards.filter(c => masteryLevel(this.state.cards[c.id]) === 2).length;
      out.push({ cat, total: cards.length, mastered });
    }
    return out;
  }

  globalStats(): { total: number; mastered: number; learning: number; new: number } {
    const allowed = new Set<Level>(this.state.settings.allowedLevels);
    let mastered = 0, learning = 0, fresh = 0, total = 0;
    for (const c of CARDS) {
      if (!allowed.has(getLevel(c))) continue;
      total++;
      const lvl = masteryLevel(this.state.cards[c.id]);
      if (lvl === 2) mastered++;
      else if (lvl === 1) learning++;
      else fresh++;
    }
    return { total, mastered, learning, new: fresh };
  }
}

export const store = new Store();
